import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { siteConfig, programs, impactStats, aboutContent } from '@/data/mock';
import { Calendar, MapPin, ArrowRight } from 'lucide-react';
import usePageTitle from '@/hooks/usePageTitle';
import usePageMeta from '@/hooks/usePageMeta';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HomePage = () => {
  usePageTitle(null);
  usePageMeta({
    title: 'ElevateMN - Mental Health & Community Support',
    description: 'ElevateMN is a mental health and community support organization in Minnesota. Programs, workshops, retreats, and events focused on healing, connection, and elevation.',
    image: '/images/site/logo.png',
    url: 'https://elevatingmn.org',
  });
  // Show first 3 programs on homepage
  const featuredPrograms = programs.slice(0, 3);

  // Inject JSON-LD structured data for SEO
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "ElevateMN",
      "url": "https://elevatingmn.com",
      "logo": window.location.origin + "/logo.png",
      "description": "Mental health and community support organization based in Maple Grove, MN. Programs, workshops, retreats, and events focused on healing, connection, and elevation.",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Maple Grove",
        "addressRegion": "MN",
        "addressCountry": "US"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "email": "admin@elevatingmn.com",
        "telephone": "612-554-1917",
        "contactType": "General Inquiries"
      },
      "sameAs": []
    });
    document.head.appendChild(script);
    return () => { if (script.parentNode) script.parentNode.removeChild(script); };
  }, []);

  const [missionRef, missionVisible] = useScrollReveal();
  const [programsRef, programsVisible] = useScrollReveal();
  const [impactRef, impactVisible] = useScrollReveal();
  const [ctaRef, ctaVisible] = useScrollReveal();
  const [galleryPhotos, setGalleryPhotos] = useState([]);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/gallery?visible_only=true`);
        if (res.ok) {
          const data = await res.json();
          setGalleryPhotos(data.slice(0, 6));
        }
      } catch {}
    };
    fetchGallery();
  }, []);

  return (
    <div>
      {/* Event Banner */}
      <Link to="/elevated-escape/tickets" className="block group">
        <div className="relative p-[3px] overflow-hidden neon-border-wrap">
          <div className="absolute inset-0 neon-border-glow" />
          <div className="relative overflow-hidden">
          <img
            src="/images/site/hero.jpg"
            alt="Elevated Escape Camping Retreat 2026"
            className="w-full h-48 md:h-64 object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/50 flex items-center">
            <div className="mx-auto max-w-7xl px-6 lg:px-8 w-full">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <p className="text-brand-gold text-sm font-medium mb-1">COMING AUGUST 2026</p>
                  <h2 className="text-2xl md:text-3xl font-semibold text-white mb-2">
                    The Elevated Escape Camping Retreat
                  </h2>
                  <div className="flex items-center gap-4 text-white/80 text-sm">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      Aug 21-23, 2026
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      Camp Waub-O-Jeeg
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-brand-gold font-medium">
                  Learn More & Register
                  <ArrowRight className="w-5 h-5" />
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>
      </Link>

      {/* Hero Section - Dark, Text-Forward */}
      <section className="bg-brand-dark py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl lg:text-5xl font-semibold text-white leading-tight mb-6">
              {siteConfig.tagline}
            </h1>
            <p className="text-lg text-gray-400 mb-10 max-w-2xl">
              {siteConfig.description}
            </p>
            <p className="text-brand-gold/80 text-sm font-medium tracking-wide mb-10">You are not alone.</p>
            <Link
              to="/programs"
              className="inline-block bg-brand-gold px-8 py-3.5 text-base font-semibold text-brand-dark transition-opacity hover:opacity-90"
            >
              Explore Our Programs
            </Link>
          </div>
        </div>
      </section>

      {/* Mission Section - Light Background */}
      <section ref={missionRef} className={`bg-brand-light py-20 lg:py-24 transition-all duration-700 ease-out ${missionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="text-2xl lg:text-3xl font-semibold text-brand-dark mb-6">
              Our Mission
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              {aboutContent.mission}
            </p>
          </div>
        </div>
      </section>

      {/* Programs Preview Section */}
      <section ref={programsRef} className={`bg-white py-20 lg:py-24 transition-all duration-700 ease-out ${programsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mb-12">
            <h2 className="text-2xl lg:text-3xl font-semibold text-brand-dark mb-4">
              Our Programs
            </h2>
            <p className="text-gray-600 max-w-2xl">
              Programs built around connection, movement, and shared experience. We create accessible spaces where people feel supported.
            </p>
          </div>

          <div className="space-y-12">
            {featuredPrograms.map((program, index) => (
              <div key={program.id} className="border-b border-gray-200 pb-12 last:border-0">
                {/* Special layout for Men's Mental Health Resets with image on top */}
                {program.id === 1 ? (
                  <div>
                    <img
                      src="/images/site/program1.png"
                      alt="Men's Mental Health Reset - Disc Golf"
                      className="w-40 h-auto mb-6"
                    />
                    <h3 className="text-xl font-semibold text-brand-dark mb-2">
                      {program.title}
                      {program.subtitle && (
                        <span className="text-gray-500 font-normal"> — {program.subtitle}</span>
                      )}
                    </h3>
                    <p className="text-gray-600 mb-4">{program.whatItIs}</p>
                    <p className="text-sm text-gray-500">
                      <span className="font-medium">Who it's for:</span> {program.whoFor}
                    </p>
                  </div>
                ) : program.id === 2 ? (
                  <div>
                    <img
                      src="/images/site/program2.png"
                      alt="Women's Workshops & Healing Circles"
                      className="w-40 h-auto mb-6"
                    />
                    <h3 className="text-xl font-semibold text-brand-dark mb-2">
                      {program.title}
                      {program.subtitle && (
                        <span className="text-gray-500 font-normal"> — {program.subtitle}</span>
                      )}
                    </h3>
                    <p className="text-gray-600 mb-4">{program.whatItIs}</p>
                    <p className="text-sm text-gray-500">
                      <span className="font-medium">Who it's for:</span> {program.whoFor}
                    </p>
                  </div>
                ) : program.id === 3 ? (
                  <div>
                    <img
                      src="/images/site/program3.png"
                      alt="Elevated Takeover @ Ely's Peak"
                      className="w-40 h-auto mb-6"
                    />
                    <h3 className="text-xl font-semibold text-brand-dark mb-2">
                      {program.title}
                      {program.subtitle && (
                        <span className="text-gray-500 font-normal"> — {program.subtitle}</span>
                      )}
                    </h3>
                    <p className="text-gray-600 mb-4">{program.whatItIs}</p>
                    <p className="text-sm text-gray-500">
                      <span className="font-medium">Who it's for:</span> {program.whoFor}
                    </p>
                  </div>
                ) : (
                  <>
                    <h3 className="text-xl font-semibold text-brand-dark mb-2">
                      {program.title}
                      {program.subtitle && (
                        <span className="text-gray-500 font-normal"> — {program.subtitle}</span>
                      )}
                    </h3>
                    <p className="text-gray-600 mb-4">{program.whatItIs}</p>
                    <p className="text-sm text-gray-500">
                      <span className="font-medium">Who it's for:</span> {program.whoFor}
                    </p>
                  </>
                )}
              </div>
            ))}
          </div>

          <div className="mt-12">
            <Link
              to="/programs"
              className="text-brand-gold font-medium hover:underline"
            >
              View all programs →
            </Link>
          </div>
        </div>
      </section>

      {/* Community Photo Strip */}
      {galleryPhotos.length > 0 && (
      <section className="bg-brand-dark py-16 overflow-hidden">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 mb-8">
          <p className="text-brand-gold text-xs uppercase tracking-widest mb-2">Our Community</p>
          <div className="flex items-end justify-between">
            <h2 className="text-2xl font-semibold text-white">Real people. Real connection.</h2>
            <Link to="/gallery" className="text-brand-gold text-sm font-medium hover:underline hidden sm:block">View Gallery →</Link>
          </div>
        </div>
        <div className="flex gap-3 px-6 overflow-x-auto pb-4 scrollbar-hide" data-testid="community-photo-strip">
          {galleryPhotos.map((img, i) => (
            <Link to="/gallery" key={img.id || i} className="flex-shrink-0 group">
              <img src={img.src} alt={img.alt} className="h-48 md:h-56 w-auto object-cover group-hover:opacity-90 transition-opacity" loading="lazy" />
            </Link>
          ))}
        </div>
        <div className="mx-auto max-w-7xl px-6 mt-4 sm:hidden">
          <Link to="/gallery" className="text-brand-gold text-sm font-medium hover:underline">View Gallery →</Link>
        </div>
      </section>
      )}

      {/* Impact Section */}
      <section ref={impactRef} className={`bg-brand-light py-20 lg:py-24 transition-all duration-700 ease-out ${impactVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <h2 className="text-2xl lg:text-3xl font-semibold text-brand-dark mb-12">
            Our Impact
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {impactStats.map((stat, index) => (
              <div key={index}>
                <p className="text-3xl lg:text-4xl font-semibold text-brand-gold mb-2">
                  {stat.value}
                </p>
                <p className="text-sm text-gray-600">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - One per page as per guidelines */}
      <section ref={ctaRef} className={`bg-brand-gold py-16 transition-all duration-700 ease-out ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <h2 className="text-2xl font-semibold text-brand-dark mb-2">
                Ready to get involved?
              </h2>
              <p className="text-brand-dark opacity-80">
                Join our community and support mental health in Minnesota.
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-block bg-brand-dark px-8 py-3.5 text-base font-semibold text-white transition-opacity hover:opacity-90 text-center"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
